import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import clonedeep from 'lodash.clonedeep';

import createNewSub from './createNewSubscription';
import { getSubscriptions } from './getSubscriptions';
import {
  constructNewSubscription,
  getAllCategoryChannels,
  addPriorityWeeklyEmail,
} from './helpers';
import {
  setSubscription,
  removeAllSubscriptions,
  ISubscription,
  loadSubscriptions,
  EventCategories,
  IEventChannel,
} from 'redux/reducers/subscriptions';

import { getValue } from './helpers';

const formatSubscriptionToSave = ({
  entityId,
  entityType,
  countryId,
  customerId,
  eventChannels,
}: ISubscription): ISubscription => ({
  entityId,
  entityType,
  countryId,
  customerId,
  eventChannels,
});

type TParams = (
  storedSubscription: null | ISubscription,
  entityId: string
) => [
  ISubscription,
  (subscriptions?: any, selectedCustomer?: any, selectedCountry?: any) => void,
  () => void,
  () => void,
  React.Dispatch<React.SetStateAction<ISubscription>>
];

const useSettings: TParams = (storedSubscription, entityId) => {
  const dispatch = useDispatch();
  const initialSubscription =
    clonedeep(storedSubscription) || createNewSub(entityId);

  const [currentSettings, setCurrentSettings] =
    useState<ISubscription>(initialSubscription);

  const save = useCallback(
    (subscriptions, selectedCustomer, selectedCountry) => {
      // if we edit existing subscription
      if (!selectedCustomer && !selectedCountry && currentSettings) {
        const channels = currentSettings.eventChannels
          .filter((channel: any) => channel.channelId !== 'all')
          .map((event: any) => {
            return {
              ...event,
              ...(event.eventCategories.includes('priority')
                ? {
                    eventTypes: [
                      'priority_playlist_add',
                      'priority_playlist_removal',
                    ],
                    eventCategories: event.eventCategories.filter(
                      (category: EventCategories) => category !== 'priority'
                    ),
                  }
                : {}),
            };
          });

        dispatch(
          setSubscription(
            formatSubscriptionToSave({
              ...currentSettings,
              eventChannels: addPriorityWeeklyEmail({ channels }),
            }),
            loadSubscriptions
          )
        );
        return;
      }

      let newSubscriptions: ISubscription[] = [];

      /** Select only the categories, which are checked in Subscription form */
      const selectedCategories = currentSettings.eventChannels.reduce(
        (array: EventCategories[], channel: IEventChannel) => {
          return [...new Set([...array, ...channel.eventCategories])];
        },
        []
      );

      /** This is all the channels from currentSettings checked except 'all' channel.
       * This channel is only needed for displaying, but not for saving subscriptions.
       */
      const originalChannelsToSave = currentSettings.eventChannels.filter(
        (channel: any) => channel?.channelId !== 'all'
      );

      /** If only one category is checked, we do not need to compare it with any and just save it, without 'all' channel */
      if (selectedCategories.length === 1) {
        newSubscriptions = [
          {
            countryId: selectedCountry[selectedCategories[0]]?.value,
            customerId: selectedCustomer[selectedCategories[0]]?.value,
            entityId: currentSettings.entityId,
            entityType: currentSettings.entityType,
            eventChannels: addPriorityWeeklyEmail({
              channels: originalChannelsToSave,
              hasCategory: true,
            }),
          },
        ];
      } else {
        /** Construct new subscriptions and differ them with country and DSP */
        newSubscriptions = selectedCategories.reduce(
          (
            prev: ISubscription[],
            category: EventCategories,
            index: number,
            all: EventCategories[]
          ) => {
            /** Unify null and undefined values of DSPs and countries */
            const categoryCountry = getValue({
              item: selectedCountry[category],
              property: 'value',
            });
            const categoryCustomer = getValue({
              item: selectedCustomer[category],
              property: 'value',
            });
            let channels: any[] = [];

            /** We take two checked categories (for instance, thresholds and charts or charts and playlisting) and compare their countries and DSPs first and then construct the subscriptions for them */
            for (let id = 0; id < all.length; id++) {
              if (id + index + 1 === all.length) break;
              const comparingCategory = all[id + index + 1];
              /** Unify null and undefined values of DSPs and countries for the second category in a pair */
              const comparingCategoryCountry = getValue({
                item: selectedCountry[comparingCategory],
                property: 'value',
              });
              const comparingCategoryCustomer = getValue({
                item: selectedCustomer[comparingCategory],
                property: 'value',
              });

              /** If the countries and the DSPs for both taken categories are the same, go forward */
              if (
                categoryCountry === comparingCategoryCountry &&
                categoryCustomer === comparingCategoryCustomer
              ) {
                /** Find all the checked channels for category and comparingCategory
                 * save them in channels variable
                 */
                // eslint-disable-next-line no-loop-func
                originalChannelsToSave.forEach((channel: IEventChannel) => {
                  if (channel.eventCategories.includes(category)) {
                    channels = getAllCategoryChannels({
                      channels,
                      category,
                      channel,
                    });
                  }
                  if (channel.eventCategories.includes(comparingCategory)) {
                    channels = getAllCategoryChannels({
                      channels,
                      category: comparingCategory,
                      channel,
                    });
                  }
                });

                if (channels.length > 0) {
                  constructNewSubscription({
                    prev,
                    countryId: categoryCountry,
                    customerId: categoryCustomer,
                    channels,
                    entityId: currentSettings.entityId,
                    entityType: currentSettings.entityType,
                  });
                }
              } else {
                channels = [];
                const channelsCategory = originalChannelsToSave
                  .filter((channel: IEventChannel) =>
                    channel.eventCategories.includes(category)
                  )
                  .map((channel: IEventChannel) => ({
                    channelId: channel.channelId,
                    eventCategories: [category],
                  }));
                const channelsComparingCategory = originalChannelsToSave
                  .filter((channel: IEventChannel) =>
                    channel.eventCategories.includes(comparingCategory)
                  )
                  .map((channel: IEventChannel) => ({
                    channelId: channel.channelId,
                    eventCategories: [comparingCategory],
                  }));

                if (channelsCategory.length > 0) {
                  constructNewSubscription({
                    prev,
                    countryId: categoryCountry,
                    customerId: categoryCustomer,
                    channels: channelsCategory,
                    entityId: currentSettings.entityId,
                    entityType: currentSettings.entityType,
                  });
                }

                if (channelsComparingCategory.length > 0) {
                  constructNewSubscription({
                    prev,
                    countryId: comparingCategoryCountry,
                    customerId: comparingCategoryCustomer,
                    channels: channelsComparingCategory,
                    entityId: currentSettings.entityId,
                    entityType: currentSettings.entityType,
                  });
                }
              }
            }

            return prev;
          },
          []
        );
      }

      // Get the subscription array ready to save
      const events = getSubscriptions({ newSubscriptions, subscriptions });

      events.forEach((event: ISubscription) => {
        dispatch(
          setSubscription(
            formatSubscriptionToSave({
              entityId: currentSettings?.entityId,
              entityType: currentSettings?.entityType,
              eventChannels: event?.eventChannels,
              countryId: event?.countryId,
              customerId: event?.customerId,
            }),
            loadSubscriptions
          )
        );
      });
    },
    [dispatch, currentSettings]
  );

  const unsubscribe = useCallback(() => {
    dispatch(removeAllSubscriptions(entityId));
  }, [dispatch, entityId]);

  const cancel = useCallback(() => {
    setCurrentSettings(initialSubscription);
  }, [initialSubscription]);
  return [currentSettings, save, cancel, unsubscribe, setCurrentSettings];
};

export default useSettings;
